import { Dispatch, FC, SetStateAction, useState } from 'react';
import { SidebarTabLayout } from '../shared/SidebarTabLayout';
import {
  Avatar,
  Button,
  Dropdown,
  Icon,
  TabContent,
  Tabs,
  TimeAgo,
} from '../../../../../shared/ui';
import { IconMap } from '../../../../../shared/sprite';
import { useDispatch, useSelector } from 'react-redux';
import { callModel, CallTranscript } from '../../../../../features/call';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import {
  checkIsMuxFailed,
  checkIsMuxLoading,
  getMuxAssetImageByPolicy,
  getMuxAssetStreamByPolicy,
} from '@distribute/shared/utils';

import { MEETING_PLATFORMS } from '../../../../../entities/call-recordings';

import { CallSummaryView } from './../../../../../features/call/ui/CallSummaryView';
import { InsertVariant } from './lib/config';
import { InsertCallButton } from './InsertCallButton';

import {
  CALL_DETAIL_TABS,
  CallDetailTab,
} from '../../../../../entities/call-recordings';

import { VideoPlayer } from '../../../../../entities/video';

type Props = {
  setInsertVariant: Dispatch<SetStateAction<InsertVariant | null>>;
  setMakePublicConfirmationCallId: Dispatch<SetStateAction<string | null>>;
};

export const CallPreview: FC<Props> = ({
  setInsertVariant,
  setMakePublicConfirmationCallId,
}) => {
  const dispatch = useDispatch();

  const currentCall = useSelector(
    callModel.selectors.selectCurrentCallWithError
  );

  const [selectedTab, setSelectedTab] = useState<TabContent>(
    CALL_DETAIL_TABS[0]
  );

  const currentPlatform =
    MEETING_PLATFORMS[currentCall.platform] ?? MEETING_PLATFORMS.googleMeet;

  const attendeesItems = currentCall.attendees.map((attendee) => {
    return {
      label: (
        <div key={attendee.id} className="flex items-center gap-2">
          <Avatar
            className="border border-base-black/[0.08]"
            src={undefined}
            displayName={attendee.name}
            size="sm"
          />
          <p className="text-sm font-medium text-gray-900 truncate">
            {attendee.name}
          </p>
        </div>
      ),
      id: attendee.id.toString(),
    };
  });

  const handleClose = () => {
    dispatch(callModel.actions.setCurrentCall(undefined));
  };

  return (
    <SidebarTabLayout
      headerComponent={
        <div className="flex items-center justify-between">
          <Button
            size="md"
            color="secondary"
            variant="icon"
            onClick={handleClose}
          >
            <Icon glyph={IconMap.ArrowLeft} width={20} />
          </Button>
          <InsertCallButton
            call={currentCall}
            setInsertVariant={setInsertVariant}
            setMakePublicConfirmationCallId={setMakePublicConfirmationCallId}
            isPreview
          />
        </div>
      }
    >
      <p className="mb-1 text-2xl font-semibold text-gray-900">
        {currentCall.name}
      </p>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-gray-600">
          <span className="shadow-xs">
            <Icon glyph={currentPlatform.icon} width={20} />
          </span>
          <span className="text-sm text-gray-600">{currentPlatform.name}</span>
          <Icon glyph={IconMap.Dot} width={5} />
          <TimeAgo datetime={currentCall.createdAt} locale="my" />
        </div>
        <Dropdown
          items={attendeesItems}
          listStyles="shadow-lg"
          isModalMode={false}
          triggerComponent={
            <DropdownMenu.Trigger>
              <div className="flex gap-1.5 p-1 shadow-xs bg-gray-100 rounded-full items-center">
                <Icon
                  glyph={IconMap.Lead}
                  width={16}
                  className="text-gray-500"
                />
                <span className="text-sm font-medium text-gray-500">
                  {currentCall.attendees.length} people
                </span>
                <Icon
                  glyph={IconMap.ChevronDown}
                  width={12}
                  className="text=gray-700"
                />
              </div>
            </DropdownMenu.Trigger>
          }
        />
      </div>
      <div className="flex flex-col w-full gap-6 mb-8 max-w-160">
        <VideoPlayer
          isLoading={checkIsMuxLoading(currentCall.muxAsset)}
          isFailed={checkIsMuxFailed(currentCall.muxAsset)}
          url={getMuxAssetStreamByPolicy(currentCall.muxAsset)}
          posterUrl={getMuxAssetImageByPolicy(currentCall.muxAsset)}
          aspectRatio={currentCall.muxAsset.data.aspectRatio}
          hideBanner
        />
        <div className="flex flex-col flex-1">
          <Tabs
            className="!mb-4 bg-base-white !h-8 sticky top-0"
            tabs={CALL_DETAIL_TABS}
            onSelectTab={setSelectedTab}
            selectedTab={selectedTab}
          >
            {selectedTab.name === CallDetailTab.SUMMARY && (
              <CallSummaryView call={currentCall} isHideControlButtons />
            )}

            {selectedTab.name === CallDetailTab.TRANSCRIPT && (
              <CallTranscript call={currentCall} isHideControlButtons />
            )}
          </Tabs>
        </div>
      </div>
    </SidebarTabLayout>
  );
};
